import type { ReactNode } from 'react'
import { Suspense, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { UserStatus } from '@portal/pkg-api/gen/admin-console'
import { emptyFn } from '@portal/pkg-utils'
import { OrgBar } from './OrgBar'
import { UsersTable } from './UsersTable'
import { useExportUsersCsv } from '../mutations'
import { UserQueries } from '../queries'

interface UsersPageProps {
  children?: ReactNode
  orgId?: number
  effectiveUserOrgId?: number
  canManageAllOrgs?: boolean
  onOrgChange?: (orgId: number | null) => void
}

export function UsersPage({
  children,
  orgId,
  effectiveUserOrgId,
  canManageAllOrgs = false,
  onOrgChange = emptyFn,
}: UsersPageProps) {
  const { t } = useTranslation('admin-console')
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const exportCsv = useExportUsersCsv()
  const effectiveOrgId = canManageAllOrgs ? orgId : undefined
  const { data: orgSettings } = useSuspenseQuery(UserQueries.orgSettings(effectiveOrgId))
  const { data: userList } = useQuery(UserQueries.list(effectiveOrgId))
  const activeUserCount = userList?.filter((u) => u.status === UserStatus.ENABLED).length
  const selectedOrgId = canManageAllOrgs ? (orgId ?? effectiveUserOrgId ?? null) : null

  return (
    <Stack
      spacing={2}
      sx={{
        px: 2,
        pt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          {t('users.page.title')}
        </Typography>
        <Stack direction="row" spacing={1}>
          {/* Action buttons will go here */}
        </Stack>
      </Box>

      {canManageAllOrgs && <OrgBar selectedOrgId={selectedOrgId} onOrgChange={onOrgChange} />}

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder={t('users.page.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
          />
          {canManageAllOrgs && activeUserCount !== undefined && (
            <Chip
              label={t('users.orgBar.activeUsers', { count: activeUserCount })}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={exportCsv.isPending}
            onClick={() => exportCsv.mutate(effectiveOrgId)}
            sx={{
              backgroundColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { backgroundColor: 'grey.400' },
            }}
          >
            {t('users.page.exportCsv')}
          </Button>
          {orgSettings.enableAdd && (
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                void navigate({
                  to: '/preferences/users/new',
                })
              }
            >
              {t('users.page.addUser')}
            </Button>
          )}
        </Stack>
      </Box>
      <Suspense fallback={<CircularProgress size={24} sx={{ mt: 2 }} />}>
        <UsersTable search={search} orgId={effectiveOrgId} />
      </Suspense>
      {children}
    </Stack>
  )
}
